import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import type {
  CreateClientAbsenceOptions,
  DeleteClientAbsencePeriodOptions,
  EndClientAbsencePeriodOptions,
  UpdateClientAbsenceOptions
} from 'RestAPIs/absence';
import {
  createClientAbsenceRequest,
  deleteClientAbsencePeriod,
  endClientAbsencePeriod,
  updateClientAbsence
} from 'RestAPIs/absence';
import type {
  ConnectAppointmentGroupingPayload,
  ConnectSchedulePayload,
  CreateAppointmentPayload,
  EndAppointmentsPayload,
  UpdateRouteStartPayload
} from 'RestAPIs/activities';
import {
  connectAppointmentGrouping,
  connectSchedule,
  createAppointment,
  endAppointments,
  updateRouteStart
} from 'RestAPIs/activities';

import { showSnackbar } from 'components/shared';
import showError from 'components/shared/showError';

// The connect mutations own the failure snackbar: a refused connect (a 409 with
// a known backend code) gets its message, anything else the generic one. The
// per-call `onError` the views pass only resets their own drag state and must
// not show a second snackbar.
export const useConnectAppointmentGrouping = ({
  onSuccess
}: {
  onSuccess?: (data) => void;
}) =>
  useMutation(
    (payload: ConnectAppointmentGroupingPayload) =>
      connectAppointmentGrouping(payload),
    { onError: showError, onSuccess }
  );

export const useConnectSchedule = ({
  onSuccess
}: {
  onSuccess?: (data) => void;
}) =>
  useMutation((payload: ConnectSchedulePayload) => connectSchedule(payload), {
    onError: showError,
    onSuccess
  });

export const useUpdateRouteStart = () => {
  const { t } = useTranslation();

  return useMutation(
    ({
      appointmentGroupingStart,
      appointmentGroupingId,
      editAppointmentType,
      suppressWarnings,
      start
    }: UpdateRouteStartPayload) =>
      updateRouteStart({
        originalAppointmentGroupingStart: appointmentGroupingStart,
        appointmentGroupingId,
        editAppointmentType,
        suppressWarnings,
        start
      }),
    {
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    }
  );
};

export const useEndAppointments = () => {
  const { t } = useTranslation();

  return useMutation(
    ({ date, appointmentIds }: EndAppointmentsPayload) =>
      endAppointments({
        date,
        appointmentIds
      }),
    {
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    }
  );
};

export const useCreateAppointment = () => {
  const { t } = useTranslation('common');
  return useMutation(
    async ({
      start,
      durationInSeconds,
      activityEntityId,
      organizationalUnitId,
      description,
      recurrenceRule
    }: CreateAppointmentPayload) =>
      await createAppointment({
        start,
        durationInSeconds,
        activityEntityId,
        organizationalUnitId,
        description,
        recurrenceRule
      }),
    {
      onSuccess: () => {
        showSnackbar({ severity: 'success', message: t('changesSaved') });
      },
      onError: () => {
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        });
      }
    }
  );
};

const CLIENT_ABSENCES_QUERY_KEY = 'client-absences';

export const useCreateAbsence = () => {
  const { t } = useTranslation();
  const QueryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateClientAbsenceOptions) => {
      await createClientAbsenceRequest(params);
    },
    onSuccess: async (_, context) => {
      QueryClient.invalidateQueries(CLIENT_ABSENCES_QUERY_KEY);

      if (context?.onSuccess) context.onSuccess();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
  });
};

export const useUpdateClientAbsence = () => {
  const { t } = useTranslation();
  const QueryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateClientAbsenceOptions) => {
      await updateClientAbsence(params);
    },
    onSuccess: async (_, context) => {
      QueryClient.invalidateQueries(CLIENT_ABSENCES_QUERY_KEY);

      if (context?.onSuccess) context.onSuccess();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
  });
};

export const useEndClientAbsencePeriod = () => {
  const { t } = useTranslation();
  const QueryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: EndClientAbsencePeriodOptions) => {
      await endClientAbsencePeriod(params);
    },
    onSuccess: async (_, context) => {
      QueryClient.invalidateQueries(CLIENT_ABSENCES_QUERY_KEY);
      if (context?.onSuccess) context.onSuccess();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
  });
};

export const useDeleteClientAbsencePeriod = () => {
  const { t } = useTranslation();
  const QueryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteClientAbsencePeriodOptions) => {
      await deleteClientAbsencePeriod(params);
    },
    onSuccess: async (_, context) => {
      QueryClient.invalidateQueries(CLIENT_ABSENCES_QUERY_KEY);

      if (context?.onSuccess) context.onSuccess();
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }
  });
};
